import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Lets the site owner exclude their own browsing from every analytics system
// (native dashboard, GA4, Meta Pixel) with a one-time visit to a URL — no
// login required, since the owner browses the storefront as a normal
// visitor most of the time, not always logged into /admin.
// Visit  ?xpz_owner=1  once on a device to opt that browser out permanently
// (stored in localStorage AND a cookie, survives across sessions/tabs on
// that device).
// Visit  ?xpz_owner=0  to turn tracking back on for that device (e.g. to
// test the site as a real customer would see it).
//
// IMPORTANT: the live site is reachable on BOTH xpeditionz.co.nz and
// www.xpeditionz.co.nz with no redirect unifying them into one origin, and
// localStorage never shares between two different hosts. So the flag is
// ALSO stored as a cookie scoped to the shared parent domain
// (.xpeditionz.co.nz) — cookies (unlike localStorage) can be scoped to a
// parent domain, so a cookie set on either host is sent on both, closing
// the gap that let the owner's visits still get tracked on the other host.
const OWNER_EXCLUDE_KEY = "xpz_owner_exclude";

// Only widen the cookie to the parent domain on the real production hosts —
// never on the preview/staging domain, where doing so would scope the
// cookie too broadly across unrelated apps on the same platform domain.
function ownerCookieDomain() {
  const host = window.location.hostname;
  if (host === "xpeditionz.co.nz" || host.endsWith(".xpeditionz.co.nz")) return ".xpeditionz.co.nz";
  return null;
}

function setOwnerCookie(value, maxAgeSeconds) {
  const domain = ownerCookieDomain();
  let cookie = `${OWNER_EXCLUDE_KEY}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  if (window.location.protocol === "https:") cookie += "; Secure";
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

function getOwnerCookie() {
  const match = document.cookie.match(new RegExp(`(?:^|; )${OWNER_EXCLUDE_KEY}=([^;]*)`));
  return match ? match[1] : null;
}

function processOwnerExcludeParam() {
  const params = new URLSearchParams(window.location.search);
  if (params.has("xpz_owner")) {
    if (params.get("xpz_owner") === "0") {
      localStorage.removeItem(OWNER_EXCLUDE_KEY);
      setOwnerCookie("0", 0);
      axios.post(`${API}/api/analytics/include-visitor`, { visitor_id: getVisitorId() }).catch(() => {});
    } else {
      localStorage.setItem(OWNER_EXCLUDE_KEY, "1");
      setOwnerCookie("1", 60 * 60 * 24 * 3650); // ~10 years
      // Server-side twin of the cookie/localStorage above — registers this
      // browser's persistent visitor_id so it's excluded from every report
      // no matter what happens to this cookie/localStorage later (Safari's
      // 7-day cap on JS-set cookies, cleared site data, a new profile, etc).
      axios.post(`${API}/api/analytics/exclude-visitor`, { visitor_id: getVisitorId() }).catch(() => {});
    }
    return;
  }
  // Backfill: a device that opted out BEFORE this fix shipped has the flag
  // in localStorage but never got the shared-domain cookie written, so it
  // still wouldn't apply on the other host. Write it now, on any normal
  // visit, so the owner doesn't have to revisit the opt-out URL again.
  if (localStorage.getItem(OWNER_EXCLUDE_KEY) === "1" && !getOwnerCookie()) {
    setOwnerCookie("1", 60 * 60 * 24 * 3650);
  }
  // Also re-registers server-side on every normal visit while opted out —
  // covers a device that opted out before the server-side safety net above
  // existed, and re-heals if the visitor_id ever needed to regenerate.
  // (Deliberately not using isOwnerExcluded() here — that also matches
  // navigator.webdriver, and automated test sessions don't need a
  // permanent entry in the excluded_visitor_ids collection.)
  if (localStorage.getItem(OWNER_EXCLUDE_KEY) === "1" || getOwnerCookie() === "1") {
    axios.post(`${API}/api/analytics/exclude-visitor`, { visitor_id: getVisitorId() }).catch(() => {});
  }
}

function isOwnerExcluded() {
  // `navigator.webdriver` is set by every WebDriver-controlled browser
  // (Playwright, Puppeteer, Selenium) regardless of user-agent string, so
  // this reliably excludes automated testing sessions from analytics too —
  // not just the site owner's own manual opt-out.
  return localStorage.getItem(OWNER_EXCLUDE_KEY) === "1" || getOwnerCookie() === "1" || navigator.webdriver === true;
}

export function getSessionId() {
  let id = sessionStorage.getItem("xpz_session_id");
  if (!id) {
    id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem("xpz_session_id", id);
  }
  return id;
}

// Persists indefinitely (unlike the per-tab session id above) — lets the
// Admin dashboard tell New vs Returning visitors apart across separate days.
function getVisitorId() {
  let id = localStorage.getItem("xpz_visitor_id");
  if (!id) {
    id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("xpz_visitor_id", id);
  }
  return id;
}

function getScrollDepthPercent() {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - doc.clientHeight;
  if (scrollable <= 0) return 100;
  return Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100));
}

function sendExitBeacon(path, durationSeconds, scrollDepthPercent) {
  if (!path || durationSeconds < 1 || isOwnerExcluded()) return;
  const payload = JSON.stringify({
    path,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    event_type: "exit",
    duration_seconds: durationSeconds,
    scroll_depth_percent: scrollDepthPercent,
  });
  if (navigator.sendBeacon) {
    navigator.sendBeacon(`${API}/api/analytics/track`, new Blob([payload], { type: "application/json" }));
  } else {
    axios.post(`${API}/api/analytics/track`, JSON.parse(payload)).catch(() => {});
  }
}

// Fires a one-off "click" event (e.g. Buy Now / Enquiry submit) — powers the
// Admin Panel's "Top Clicked Buttons" behaviour report.
export function trackEvent(eventName) {
  if (isOwnerExcluded()) return;
  axios.post(`${API}/api/analytics/track`, {
    path: window.location.pathname,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    event_type: "click",
    event_name: eventName,
  }).catch(() => {});
}

// Fires a Meta (Facebook/Instagram) Pixel event — no-op until the admin has
// saved a Pixel ID AND switched it on via Admin -> Settings -> Meta Pixel
// (dormant by default, same pattern as Google Analytics below). Also mirrors
// the same event to the backend's Meta Conversions API (server-side) using
// the SAME event_id, so Meta deduplicates the two into one event while
// recovering conversions the browser pixel alone would miss (ad blockers,
// iOS privacy restrictions). Pass an explicit `eventId` when a caller
// already has one tied to real server-side data (e.g. Lead's
// `meta_event_id` from the /enquiry response, or a checkout `session_id` for
// Purchase) — otherwise one is generated here. Pass `custom=true` for a
// non-standard event name (e.g. "BushCabinProductClick") — Meta's pixel
// requires `trackCustom` rather than `track` for anything outside its
// predefined standard-event list.
export function trackMetaEvent(eventName, params, eventId, custom) {
  const id = eventId || ((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  if (isOwnerExcluded()) return id;
  if (window.fbq) {
    window.fbq(custom ? "trackCustom" : "track", eventName, params, { eventID: id });
  }
  axios.post(`${API}/api/meta/capi/event`, {
    event_name: eventName,
    event_id: id,
    event_source_url: window.location.href,
    custom_data: params || {},
  }).catch(() => {});
  return id;
}

// Self-hosted, native pageview + time-on-page + scroll-depth tracking —
// powers the Admin Panel's Analytics dashboard (Sales/Traffic/Behaviour/
// Abandoned Carts). No external service required.
const useNativeAnalytics = () => {
  const location = useLocation();
  const prevPageRef = useRef(null); // { path, enteredAt, maxScroll, hiddenMsAtEnter }
  // Total ms the tab has spent hidden/backgrounded since this component
  // mounted (i.e. since app load) — used to subtract idle/background time
  // out of "time on page" beacons, so a tab left open in the background for
  // hours doesn't get counted as hours of genuine on-page time.
  const hiddenMsRef = useRef(0);
  const hiddenSinceRef = useRef(document.visibilityState === "hidden" ? Date.now() : null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current !== null) {
        hiddenMsRef.current += Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const activeDurationSeconds = (enteredAt, hiddenMsAtEnter) => {
    let hiddenMs = hiddenMsRef.current - hiddenMsAtEnter;
    if (hiddenSinceRef.current !== null) hiddenMs += Date.now() - hiddenSinceRef.current;
    return Math.round(Math.max(0, (Date.now() - enteredAt) - hiddenMs) / 1000);
  };

  useEffect(() => {
    // Report time spent + max scroll depth on the PREVIOUS page before
    // switching to this one.
    if (prevPageRef.current) {
      const { path, enteredAt, maxScroll, hiddenMsAtEnter } = prevPageRef.current;
      sendExitBeacon(path, activeDurationSeconds(enteredAt, hiddenMsAtEnter), maxScroll);
      prevPageRef.current = null;
    }

    if (location.pathname.startsWith("/admin") || isOwnerExcluded()) return;

    const params = new URLSearchParams(window.location.search);
    axios.post(`${API}/api/analytics/track`, {
      path: location.pathname,
      referrer: document.referrer || null,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      gclid: params.get("gclid") || null,
      fbclid: params.get("fbclid") || null,
    }).catch(() => {});
    prevPageRef.current = { path: location.pathname, enteredAt: Date.now(), maxScroll: getScrollDepthPercent(), hiddenMsAtEnter: hiddenMsRef.current };

    const handleScroll = () => {
      if (prevPageRef.current) {
        prevPageRef.current.maxScroll = Math.max(prevPageRef.current.maxScroll, getScrollDepthPercent());
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // Catches the LAST page of the browser session (tab close/refresh), which
  // the route-change effect above can never see on its own.
  useEffect(() => {
    const handlePageHide = () => {
      if (prevPageRef.current) {
        const { path, enteredAt, maxScroll, hiddenMsAtEnter } = prevPageRef.current;
        sendExitBeacon(path, activeDurationSeconds(enteredAt, hiddenMsAtEnter), maxScroll);
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, []);
};

// Loads the REAL GA4 gtag.js — only if the admin has saved a Measurement ID
// AND switched it on via Admin → Settings → Google Analytics (dormant by
// default, see AdminSettingsPage.jsx). Fully separate from the native
// tracking above; reports pageviews on every route change once loaded.
let siteSettingsCache = null;
const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    if (window._gaLoaded || window._gaLoading || isOwnerExcluded()) return;
    window._gaLoading = true;
    const load = (settings) => {
      window._gaLoading = false;
      if (!settings?.gaEnabled || !settings?.gaMeasurementId) return;
      window._gaLoaded = true;
      const gaId = settings.gaMeasurementId;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", gaId, { send_page_view: false });
    };

    if (siteSettingsCache) { load(siteSettingsCache); return; }
    axios.get(`${API}/api/site-settings`).then((r) => { siteSettingsCache = r.data; load(r.data); }).catch(() => { window._gaLoading = false; });
  }, []);

  useEffect(() => {
    if (!window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);
};

// Loads the REAL Meta (Facebook/Instagram) Pixel — only if the admin has
// saved a Pixel ID AND switched it on via Admin -> Settings -> Meta Pixel
// (dormant by default, see AdminSettingsPage.jsx). Fires PageView on every
// route change, same shape as the GA4 loader above. Standard e-commerce
// events (ViewContent/AddToCart/InitiateCheckout/Purchase/Lead) are fired
// from the relevant components via trackMetaEvent() — see useRecentlyViewed,
// BuyNowButton, EnquiryForm and CheckoutSuccessPage.
const useMetaPixel = () => {
  const location = useLocation();

  useEffect(() => {
    if (window._metaPixelLoaded || window._metaPixelLoading || isOwnerExcluded()) return;
    window._metaPixelLoading = true;
    const load = (settings) => {
      window._metaPixelLoading = false;
      if (!settings?.metaPixelEnabled || !settings?.metaPixelId) return;
      window._metaPixelLoaded = true;
      const pixelId = settings.metaPixelId;
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
      document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq("init", pixelId);
      window.fbq("track", "PageView");
      window._metaPixelLastPath = window.location.pathname;
    };

    if (siteSettingsCache) { load(siteSettingsCache); return; }
    axios.get(`${API}/api/site-settings`).then((r) => { siteSettingsCache = r.data; load(r.data); }).catch(() => { window._metaPixelLoading = false; });
  }, []);

  useEffect(() => {
    if (!window.fbq || !window._metaPixelLoaded) return;
    // Guard against re-firing PageView for the same path the init call above
    // already reported (e.g. if this effect re-runs before the pixel script
    // has finished loading).
    if (window._metaPixelLastPath === location.pathname) return;
    window._metaPixelLastPath = location.pathname;
    window.fbq("track", "PageView");
  }, [location]);
};

// Pings every ~8s while a storefront tab is visible — powers the Admin
// Panel's Live tab (who's on the site right now, and what page). Skips
// hidden/backgrounded tabs so idle browser tabs don't count as "live".
const useLiveHeartbeat = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || isOwnerExcluded()) return;

    const sendHeartbeat = () => {
      if (document.visibilityState !== "visible") return;
      axios.post(`${API}/api/analytics/heartbeat`, {
        session_id: getSessionId(),
        path: window.location.pathname,
      }).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 8000);
    return () => clearInterval(interval);
  }, [location.pathname]);
};

export const AnalyticsTracker = () => {
  processOwnerExcludeParam();
  useGoogleAnalytics();
  useMetaPixel();
  useNativeAnalytics();
  useLiveHeartbeat();
  return null;
};
